import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import ScoreboardCard from '../../workspace/ScoreboardCard';
import {
  CalendarClock,
  CalendarPlus,
  FileSignature,
  MessageCircleHeart,
  MessageSquare,
  PartyPopper,
  Star,
  UserCheck,
  UserPlus,
  Zap,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { fullLeadName } from '../boardUtils';
import { isOwnedBy } from '../ownership';
import { LegacyTag, isLegacy } from '../../scheduling/MeetingActions';
import {
  Counter,
  FilterChip,
  NotesButton,
  PANEL,
  PinStar,
  PlainHead,
  ScheduleCard,
  SortHead,
  TodayCard,
  TodoMark,
  firstOf,
  initialsOf,
  todosByPerson,
  yourMoveWords,
  FILTERED_PANEL,
  FilteredPill,
} from '../../workspace/DeskKit';
import {
  useAddSalesTodo,
  useDeleteSalesTodo,
  useSaveBoardPrefs,
  useSalesTodos,
  useSetSalesPinned,
  useUpdateSalesTodo,
} from '../../../../services/onboardingBoard';
import {
  CARD_TONE,
  SignedTodayRow,
  STATIONS,
  cardRank,
  chipOf,
  isMet,
  meetingOf,
  meetingWhen,
  stageOf,
  stationOf,
  toneOfChip,
} from './signingUpUtils';
import DirectSignupPill from '../DirectSignupPill';

// Signing Up's desk (Nic 2026-10-21: "use the same page we have for
// Clients"). The Onboarding Workspace's Clients tab, for leads: counters
// that are filters, one list of everyone being signed up with where they
// are and what's next, and a rail with the Schedule, Today (the page's
// suggestions plus their own to-dos) and today's signups. A row opens the
// Workspace, the same as the cards did (BoardPage openSigningUpRun).
// Replaced the lanes of cards (PotentialClientsView) the same day.

// Where they are, as one word, one color each.
const STAGE = {
  tobook: { cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  booked: { cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  link: { cls: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  account: { cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' },
  agreement: { cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300' },
};
const stationName = (key) => STATIONS.find((s) => s.key === key)?.name || 'Book or sign up';

// Each suggestion's kind (its autoKey prefix): an icon and a tint.
const KIND_OF = {
  meet: { icon: CalendarClock, tile: 'bg-sky-50 text-sky-600 dark:!bg-sky-400/10 dark:text-sky-300' },
  facts: { icon: Zap, tile: 'bg-rose-50 text-rose-600 dark:!bg-rose-400/10 dark:text-rose-300' },
  book: { icon: CalendarPlus, tile: 'bg-amber-50 text-amber-600 dark:!bg-amber-400/10 dark:text-amber-300' },
  sign: { icon: FileSignature, tile: 'bg-indigo-50 text-indigo-600 dark:!bg-indigo-400/10 dark:text-indigo-300' },
  nudge: { icon: MessageSquare, tile: 'bg-teal-50 text-teal-600 dark:!bg-teal-400/10 dark:text-teal-300' },
};

// The last time they called or texted in, as an age.
const heardTone = (d) =>
  d == null ? 'text-slate-400' : d >= 3 ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-600 dark:text-emerald-300';
const heardDot = (d) => (d == null ? 'bg-slate-300 dark:bg-slate-600' : d >= 3 ? 'bg-amber-500' : 'bg-emerald-500');
const heardText = (d) => (d == null ? 'Not yet' : d === 0 ? 'Today' : d === 1 ? '1 day' : `${d} days`);

const SORTS = {
  attention: null,
  name: (a, b) => a.name.localeCompare(b.name),
  left: (a, b) => (b.run.checkpoint?.left ?? 99) - (a.run.checkpoint?.left ?? 99),
  heard: (a, b) => (b.heardDays ?? 999) - (a.heardDays ?? 999),
};

// Today's suggestions from the viewer's own rows: meetings today, Details
// in, and every row that needs them today, in the chip's own words.
// Each one carries `done` (Nic 2026-11-03), read from the fact it is about,
// so it checks itself off and stays on the list for the day:
//   Meeting today   met: marked met, or 30 minutes past the start with no
//                   missed mark. A no-show is not done (it says Missed and
//                   the Rebook ask takes over).
//   Confirm facts   the rep confirmed them (run.factsConfirmedAt)
//   Nudge           the nudge text went out today (run.lastNudgeAt)
//   Book or sign up a meeting was booked today (run.booking.bookedAt); a
//                   signup takes them off the list (Signed today has them)
//   Send / Sign up  the agreement or the signup link went out today
// The Nudge button on a Book or sign up row never counts: the to-do is the
// booking or the signup (Nic 2026-11-03).
const MET_AFTER_MINUTES = 30;
const meetingStateOf = (b, now) => {
  const missed = b.status === 'no_show' || !!b.noShowAt || b.outcome === 'no_show' || b.outcome === 'missed';
  if (missed) return 'missed';
  if (b.outcome === 'met' || b.outcome === 'held') return 'met';
  return moment(b.startTime).add(MET_AFTER_MINUTES, 'minutes').isSameOrBefore(moment(now)) ? 'met' : 'ahead';
};
export const suggestionsFor = (rows, now) => {
  const day = moment(now).format('YYYY-MM-DD');
  const today = (at) => !!at && moment(at).isSame(moment(now), 'day');
  const byKey = new Map();
  // A done reading wins over a live ask with the same key.
  const put = (x) => {
    const had = byKey.get(x.key);
    if (!had || (x.done && !had.done)) byKey.set(x.key, x);
  };
  rows.forEach(({ run, name, chip, ready }) => {
    const id = run.leadId;
    const trail = run.meetingTrail || {};
    // Every meeting today: the next one (ahead, or over with nothing after
    // it) and the one before it when another is booked after it.
    const todays = [
      trail.next || null,
      trail.previous ? { startTime: trail.previous.at, outcome: trail.previous.outcome, hostName: trail.previous.hostName } : null,
    ].filter((b) => b && b.startTime && today(b.startTime));
    todays.forEach((b) => {
      const state = meetingStateOf(b, now);
      // With whom, when it is not the rep (Nic 2026-10-30: a meeting booked
      // with Kit before the lead became Austin's).
      const host = b.hostName && run.ownerName && b.hostName !== run.ownerName ? ` with ${String(b.hostName).split(/\s+/)[0]}` : '';
      put({
        key: `meet:${id}:${moment(b.startTime).format('YYYY-MM-DDTHH:mm')}`,
        personId: id,
        text: `Meeting: ${name}`,
        why: state === 'missed' ? `${moment(b.startTime).format('h:mm A')}${host} · Missed` : `${moment(b.startTime).format('h:mm A')}${host}`,
        order: 0,
        at: b.startTime,
        done: state === 'met',
      });
    });
    if (ready) {
      put({ key: `facts:${id}`, personId: id, text: `Confirm ${name}'s facts`, why: 'Details in: send the agreement', order: 1, done: false });
    } else if (today(run.factsConfirmedAt)) {
      put({ key: `facts:${id}`, personId: id, text: `Confirm ${name}'s facts`, why: 'Facts confirmed', order: 1, done: true });
    }
    // What happened today, whatever the chip says now.
    if (today(run.lastNudgeAt)) {
      put({ key: `nudge:${id}:${day}`, personId: id, text: `Nudge ${name}`, why: `Nudged ${moment(run.lastNudgeAt).format('h:mm A')}`, order: 2, done: true });
    }
    if (run.booking?.status === 'active' && today(run.booking.bookedAt)) {
      put({
        key: `book:${id}:${day}`,
        personId: id,
        text: `Book or sign up ${name}`,
        why: `Booked for ${moment(run.booking.startTime).format('ddd h:mm A')}`,
        order: 2,
        done: true,
      });
    }
    if ((stageOf(run) === 'agreement_out' || stageOf(run) === 'signup_link_sent') && today(run.pipelineStageAt)) {
      put({
        key: `sign:${id}:${day}`,
        personId: id,
        text: stageOf(run) === 'agreement_out' ? `Send ${name} the agreement` : `Sign up ${name}`,
        why: stageOf(run) === 'agreement_out' ? 'Agreement sent' : 'Signup link sent',
        order: 2,
        done: true,
      });
    }
    if (ready || !chip.act) return;
    if (chip.icon === MessageSquare) {
      put({ key: `nudge:${id}:${day}`, personId: id, text: `Nudge ${name}`, why: chip.text, order: 2, done: false });
    } else if (chip.icon === CalendarPlus) {
      put({ key: `book:${id}:${day}`, personId: id, text: `Book or sign up ${name}`, why: chip.text, order: 2, done: false });
    } else {
      put({
        key: `sign:${id}:${day}`,
        personId: id,
        text: stationOf(run) === 'account' ? `Send ${name} the agreement` : `Sign up ${name}`,
        why: chip.text,
        order: 2,
        done: false,
      });
    }
  });
  return [...byKey.values()].sort((a, b) => a.order - b.order || new Date(a.at || 0) - new Date(b.at || 0));
};

// One desk row per run (the desk and the board's tab both build them).
export const deskRowsOf = (runs, now, readyRunIds = new Set(), closedUntil = null) =>
  runs.map((run) => {
    const ready = readyRunIds.has(run.cadenceRunId);
    const at = run.lastInbound?.at || null;
    return {
      run,
      name: fullLeadName(run.lead || {}) || 'Unnamed',
      station: stationOf(run),
      chip: chipOf(run, now, { ready, closedUntil }),
      meeting: meetingOf(run, now),
      ready,
      heardDays: at ? Math.max(0, moment(now).startOf('day').diff(moment(at).startOf('day'), 'days')) : null,
    };
  });

// Your move today (Nic 2026-10-23): the viewer's leads with a suggested
// to-do still open. A suggestion that checked itself off drops them from
// the number, the filter and the tab together, so the three always agree.
export const leadsNeedingYou = (rows, viewerUserId, now) => {
  const mine = rows.filter((r) => isOwnedBy(r.run.ownerUserId, viewerUserId));
  return new Set(
    suggestionsFor(mine, now)
      .filter((x) => !x.done)
      .map((x) => x.personId),
  );
};

const SigningUpDesk = ({
  runs,
  now,
  viewerUserId = null,
  // Managers, admins and view only see everyone's rows: show whose is whose.
  showOwners = false,
  canAct = false,
  detailsReady = [],
  pinned = [],
  hideDone = false,
  // The rail's Scoreboard card folded (Nic 2026-11-03), saved with the person.
  scoreboardFolded = false,
  // The board's next opening when today is closed (weekend wording).
  closedUntil = null,
  signedToday = [],
  directSignupCount = 0,
  directSignupReadyCount = 0,
  flashLeadId = null,
  // The board's one search: the runs come already searched; this says
  // which empty it is, and narrows Signed today.
  search = '',
  onOpenRun,
  onNudgeRun = null,
  onOpenMeeting,
  onOpenCalendar,
  onOpenDirectSignups,
  // The board's header line: Direct signups sits after the search there.
  headerSlots = null,
  active = false,
}) => {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'attention', dir: 'desc' });
  const pick = (f) => setFilter((cur) => (cur === f && f !== 'all' ? 'all' : f));
  const pinnedSet = useMemo(() => new Set(pinned || []), [pinned]);
  const setPinned = useSetSalesPinned();

  const readyRunIds = useMemo(() => new Set((detailsReady || []).map((d) => d.cadenceRunId)), [detailsReady]);
  const all = useMemo(() => deskRowsOf(runs, now, readyRunIds, closedUntil), [runs, now, readyRunIds, closedUntil]);
  const byLead = useMemo(() => new Map(all.map((r) => [r.run.leadId, r])), [all]);

  // Their to-dos, in the kit's words (personId = the leadId).
  const todosQ = useSalesTodos(canAct);
  const todoRows = useMemo(
    () => (todosQ.data?.todos || []).map((t) => ({ ...t, personId: t.leadId || null, personName: t.leadName || null })),
    [todosQ.data],
  );
  // Your move today: your own leads with a suggestion still open. Someone
  // else's never count, like the Onboarding Workspace (Nic 2026-11-01: an
  // admin saw 26 from the other reps' act-today chips; only the person with
  // the to-dos sees them, so a manager, an admin or view only reads 0).
  const needYou = leadsNeedingYou(all, viewerUserId, now);
  const yourMove = (r) => isOwnedBy(r.run.ownerUserId, viewerUserId) && needYou.has(r.run.leadId);

  const FILTERS = {
    all: { label: 'Everyone signing up', rows: all },
    today: { label: 'Your move today', rows: all.filter(yourMove) },
    tobook: { label: 'Book or sign up', rows: all.filter((r) => r.station === 'tobook') },
    booked: { label: 'Booked', rows: all.filter((r) => r.station === 'booked') },
    tonight: { label: 'Going to Nurture tonight', rows: all.filter((r) => r.run.checkpoint?.nurtureTonight) },
    link: { label: 'Signup link sent', rows: all.filter((r) => r.station === 'link') },
    account: { label: 'Account made', rows: all.filter((r) => r.station === 'account') },
    agreement: { label: 'Agreement sent', rows: all.filter((r) => r.station === 'agreement') },
    pinned: { label: 'Pinned', rows: all.filter((r) => pinnedSet.has(r.run.leadId)) },
  };
  const bookedToday = FILTERS.booked.rows.filter((r) => r.meeting?.today).length;

  // Default order, the cards' own: your move today, then meetings today,
  // then the least time left.
  const rows = FILTERS[filter].rows.slice().sort((a, b) => {
    if (!SORTS[sort.key]) {
      const [x1, x2] = cardRank(a.run, now);
      const [y1, y2] = cardRank(b.run, now);
      return x1 - y1 || x2 - y2;
    }
    const r = SORTS[sort.key](a, b);
    return sort.dir === 'asc' ? -r : r;
  });

  const mineOnly = all.filter((r) => isOwnedBy(r.run.ownerUserId, viewerUserId));
  const suggestions = suggestionsFor(mineOnly, now);

  const todosFor = todosByPerson(todoRows, suggestions);
  const addTodo = useAddSalesTodo();
  const updateTodo = useUpdateSalesTodo();
  const removeTodo = useDeleteSalesTodo();
  // The saved prefs go straight into the cached board, so Hide done holds
  // when the tab comes back (branch QA 2026-10-21: it flipped back until the
  // next poll).
  const queryClient = useQueryClient();
  const savePrefs = useSaveBoardPrefs((res) =>
    queryClient.setQueriesData(['onboarding-board'], (data) =>
      data && data.prefs && res?.prefs ? { ...data, prefs: res.prefs } : data,
    ),
  );

  const openLead = (leadId) => {
    const r = byLead.get(leadId);
    if (r) onOpenRun(r.run);
  };

  // A just-pulled-in row is lit and brought into view, scrolling ONLY the
  // desk (never scrollIntoView: it slides the board sideways, 2026-09-23).
  const flashRef = useRef(null);
  useEffect(() => {
    const el = flashRef.current;
    const box = el?.closest('.os-scroll');
    if (!flashLeadId || !el || !box) return;
    const r = el.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (r.top < b.top || r.bottom > b.bottom) {
      box.scrollTo({ top: box.scrollTop + (r.top - b.top) - 80, behavior: 'smooth' });
    }
  }, [flashLeadId, runs]);

  const q = search.trim().toLowerCase();
  const signedShown = signedToday.filter(
    (w) => !q || `${w.leadFirstName || ''} ${w.leadLastName || ''}`.toLowerCase().includes(q),
  );

  const head = (key, label) => <SortHead sortKey={key} label={label} sort={sort} setSort={setSort} />;
  const chip = (key, label, n) => <FilterChip key={key} on={filter === key} label={label} n={n} onClick={() => pick(key)} />;

  // Direct signups (a fallback: every signup normally goes straight to a
  // rep by the rotation). Lifted into the board's header line.
  const directButton =
    directSignupCount > 0 ? (
      <button
        type="button"
        onClick={onOpenDirectSignups}
        className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-xl bg-emerald-600 px-3.5 text-sm font-bold text-white outline-none transition-[background-color] hover:bg-emerald-500 focus:outline-none"
      >
        <UserPlus size={15} />
        {directSignupCount} direct signup{directSignupCount === 1 ? '' : 's'}
        {directSignupReadyCount > 0 ? (
          <span
            title="Confirmed their details in their signup: claim them and act now"
            className="inline-flex items-center gap-0.5 rounded-md bg-white/20 px-1.5 text-[11px] font-bold"
          >
            <Zap size={11} strokeWidth={2.75} />
            {directSignupReadyCount} ready
          </span>
        ) : null}
      </button>
    ) : null;

  const emptyText =
    q && !FILTERS[filter].rows.length
      ? 'No matches for this search.'
      : filter === 'all'
        ? 'Nobody here yet. Anyone who books a follow-up or gets a signup link lands here.'
        : filter === 'pinned'
          ? 'Nobody pinned yet. Hover a row and tap the star to keep them here.'
          : 'Nobody here right now.';

  return (
    // A little room between the right-hand tiles and the scrollbar (Nic
    // 2026-10-23).
    <div className="os-scroll min-h-0 flex-1 overflow-y-auto p-0.5 pb-4 pr-2">
      {directButton && headerSlots?.actions
        ? active
          ? createPortal(directButton, headerSlots.actions)
          : null
        : directButton
          ? <div className="mb-3 flex justify-end">{directButton}</div>
          : null}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-5">
            <Counter
              value={FILTERS.today.rows.length}
              label={yourMoveWords(closedUntil, 'Book, sign up, nudge or send').label}
              hint={yourMoveWords(closedUntil, 'Book, sign up, nudge or send').hint}
              tone={FILTERS.today.rows.length ? 'text-amber-600 dark:text-amber-300' : ''}
              on={filter === 'today'}
              onClick={() => pick('today')}
            />
            <Counter
              value={FILTERS.tobook.rows.length}
              label="Book or sign up"
              hint="No follow-up booked"
              on={filter === 'tobook'}
              onClick={() => pick('tobook')}
            />
            <Counter
              value={FILTERS.booked.rows.length}
              label="Booked"
              hint={bookedToday ? `${bookedToday} today` : 'Follow-ups ahead'}
              on={filter === 'booked'}
              onClick={() => pick('booked')}
            />
            <Counter
              value={FILTERS.tonight.rows.length}
              label="Nurture tonight"
              hint="Their last day is today"
              tone={FILTERS.tonight.rows.length ? 'text-rose-600 dark:text-rose-300' : ''}
              on={filter === 'tonight'}
              onClick={() => pick('tonight')}
            />
            <Counter
              value={FILTERS.pinned.rows.length}
              label="Pinned"
              icon={<Star size={13} className="fill-amber-400 text-amber-400" />}
              hint={FILTERS.pinned.rows.length ? 'Your quick list' : 'Star someone to pin them'}
              on={filter === 'pinned'}
              onClick={() => pick('pinned')}
            />
          </div>

          <div className={cn(PANEL, 'overflow-hidden', filter !== 'all' && FILTERED_PANEL)}>
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-white/10">
              <h3 className="mb-0 mr-2 text-[14px] font-bold text-slate-800 dark:text-slate-100">
                {FILTERS[filter].label} <span className="font-semibold tabular-nums text-slate-400">· {rows.length}</span>
              </h3>
              {filter !== 'all' ? <FilteredPill onClear={() => setFilter('all')} /> : null}
              <div className="ml-auto flex flex-wrap gap-1.5">
                {chip('all', 'All', all.length)}
                {chip('link', 'Signup link sent', FILTERS.link.rows.length)}
                {chip('account', 'Account made', FILTERS.account.rows.length)}
                {chip('agreement', 'Agreement sent', FILTERS.agreement.rows.length)}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-[13px]">
                <thead className="bg-slate-50 dark:!bg-white/[0.03]">
                  <tr>
                    {head('name', 'Lead')}
                    <PlainHead>To-dos</PlainHead>
                    <PlainHead>Where they are</PlainHead>
                    {head('left', 'Next')}
                    <PlainHead>Meeting</PlainHead>
                    {head('heard', 'Last heard')}
                    <th className="w-24 py-2.5 pl-2 pr-4">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((r) => {
                      const { run, name, chip: c, meeting } = r;
                      const tone = CARD_TONE[toneOfChip(c)];
                      const mine = isOwnedBy(run.ownerUserId, viewerUserId);
                      const flash = !!flashLeadId && run.leadId === flashLeadId;
                      const lastMeet = !meeting ? run.meetingTrail?.previous || null : null;
                      // Nudge on their last day, on their own rows, when the
                      // next move is theirs (the card's rule, 2026-10-07).
                      const canNudge =
                        !!onNudgeRun &&
                        mine &&
                        c.act &&
                        !c.ourMove &&
                        toneOfChip(c) !== 'red' &&
                        !!run.lead?.leadPhone &&
                        !run.lead?.leadSmsOptOutAt;
                      return (
                        <tr
                          key={run.cadenceRunId}
                          ref={flash ? flashRef : undefined}
                          tabIndex={0}
                          onClick={() => onOpenRun(run)}
                          onKeyDown={(e) => {
                            // Enter or Space, like the button it acts as.
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpenRun(run);
                            }
                          }}
                          className={cn(
                            'group/row cursor-pointer border-t border-slate-100 outline-none hover:bg-slate-50 focus-visible:bg-sky-50 dark:border-white/[0.06] dark:hover:!bg-white/[0.03] dark:focus-visible:!bg-sky-500/10',
                            flash && 'bg-emerald-50 dark:!bg-emerald-500/10',
                          )}
                        >
                          <td className="py-2.5 pl-2 pr-3">
                            <div className="flex items-center gap-2">
                              <PinStar
                                pinned={pinnedSet.has(run.leadId)}
                                name={name}
                                onFlip={() =>
                                  setPinned.mutate(
                                    { leadId: run.leadId, pinned: !pinnedSet.has(run.leadId) },
                                    { onError: (e) => toast.error(e?.response?.data?.msg || "That pin didn't save.") },
                                  )
                                }
                              />
                              <span
                                title={showOwners ? run.ownerName || 'No rep' : undefined}
                                className={cn(
                                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                                  showOwners
                                    ? mine
                                      ? 'bg-indigo-600 text-white dark:bg-indigo-500'
                                      : 'bg-slate-800 text-white dark:!bg-slate-200 dark:!text-slate-900'
                                    : 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                                )}
                              >
                                {showOwners ? run.ownerInitials || initialsOf(run.ownerName) : initialsOf(name)}
                              </span>
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-baseline gap-1.5">
                                  <span className="truncate font-semibold text-slate-800 dark:text-slate-100">{name}</span>
                                  {/* They made their account (Nic 2026-10-24): a quiet person
                                      mark in the name's own line, in place of the old Details
                                      in bolt (the Next column and Your move today say that). */}
                                  {run.lead?.hasAccount ? (
                                    <span title="Has an account" className="inline-flex shrink-0 self-center">
                                      <UserCheck aria-label="Has an account" size={13} strokeWidth={2.5} className="text-emerald-500 dark:text-emerald-400" />
                                    </span>
                                  ) : null}
                                  <DirectSignupPill lead={run.lead} />
                                  {stageOf(run) === 're_engaged' ? (
                                    <span className="shrink-0 text-[12px] font-medium italic tracking-[0.01em] text-sky-600 dark:text-sky-300">
                                      re-engaged
                                    </span>
                                  ) : null}
                                </div>
                                {/* Whose it is, for managers only. No "talked" date (Nic 2026-10-24):
                                    it was the day the run first reached them, which read as the last talk. */}
                                {showOwners && run.ownerName ? (
                                  <div className="truncate text-[12px] text-slate-400">
                                    {mine ? 'Yours' : `${firstOf(run.ownerName)}'s`}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <TodoMark mine={todosFor.mine.get(run.leadId)} suggested={todosFor.suggested.get(run.leadId)} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-semibold', STAGE[r.station]?.cls)}>
                              {stationName(r.station)}
                            </span>
                          </td>
                          <td className="max-w-[17rem] px-3 py-2.5" title={c.title || undefined}>
                            <span className={cn('inline-flex max-w-full items-center gap-1.5', c.act ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300')}>
                              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: tone.fill }} />
                              <span className="truncate">{c.deskText || c.text}</span>
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-600 dark:text-slate-300">
                            {meeting ? (
                              <span className={cn(meeting.today && !isMet(meeting, now) && 'font-semibold text-slate-800 dark:text-slate-100')}>
                                {isMet(meeting, now) ? `Met ${moment(meeting.at).format('MMM D')}` : meetingWhen(meeting.at, now)}
                                {/* Booked with someone else (Nic 2026-10-30): who. */}
                                {!isMet(meeting, now) && meeting.host ? <span className="text-slate-400"> with {meeting.host}</span> : null}
                                {!isMet(meeting, now) && isLegacy(meeting) ? <LegacyTag className="ml-1.5" /> : null}
                              </span>
                            ) : lastMeet ? (
                              <span className="text-slate-400">
                                {lastMeet.outcome === 'missed' ? 'Missed' : 'Met'} {moment(lastMeet.at).format('MMM D')}
                              </span>
                            ) : (
                              <span className="text-slate-300 dark:text-slate-600">None</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <span
                              title={run.lastInbound?.at ? `Last ${run.lastInbound.type === 'text' ? 'texted' : 'called'} in ${moment(run.lastInbound.at).format('ddd, MMM D h:mm A')}` : 'They have not called or texted in'}
                              className={cn('inline-flex items-center gap-1.5 font-semibold', heardTone(r.heardDays))}
                            >
                              <span className={cn('h-2 w-2 rounded-full', heardDot(r.heardDays))} />
                              {heardText(r.heardDays)}
                            </span>
                          </td>
                          <td className="py-2.5 pl-2 pr-4">
                            <div className="flex items-center justify-end gap-1.5">
                              {canNudge ? (
                                <button
                                  type="button"
                                  aria-label={`Nudge ${name}`}
                                  title="Send their nudge text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onNudgeRun(run);
                                  }}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-600 shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none ring-1 ring-teal-200/80 transition-transform duration-150 hover:-translate-y-px hover:bg-teal-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60 active:scale-95 dark:!bg-teal-400/10 dark:text-teal-300 dark:ring-teal-400/25 dark:hover:!bg-teal-400/20"
                                >
                                  <MessageCircleHeart size={15} strokeWidth={2.25} />
                                </button>
                              ) : null}
                              {String(run.lead?.leadWorkNotes || '').trim() ? (
                                <NotesButton name={name} onClick={() => onOpenRun(run, { tab: 'notes' })} />
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className={cn('px-4 py-10 text-center text-[13px]', q ? 'italic text-sky-700 dark:text-sky-300' : 'text-slate-400')}>
                        {emptyText}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Schedule first, then the to-dos, then today's wins. */}
        <aside className="flex flex-col gap-4">
          <ScheduleCard
            now={now}
            scope={showOwners ? 'all' : 'mine'}
            apiBase="/onboarding-board"
            onOpenMeeting={onOpenMeeting}
            onOpenCalendar={onOpenCalendar}
            showOwner={showOwners}
            fallbackName="Lead"
            fallbackEvent="Follow-up"
          />
          {/* The Scoreboard (Nic 2026-11-03): the team ranked, today and
              this week, above the checklist; folds, and the fold is saved. */}
          <ScoreboardCard team="sales" enabled={canAct} folded={scoreboardFolded} onFold={(next) => savePrefs.mutate({ suScoreboardFolded: next })} />
          <TodayCard
            suggestions={suggestions}
            kinds={KIND_OF}
            todos={todoRows}
            adding={addTodo.isLoading}
            onAdd={({ personId, ...rest }, opts) => addTodo.mutate({ ...rest, leadId: personId || null }, opts)}
            onUpdate={(x) => updateTodo.mutate(x)}
            onRemove={(x) => removeTodo.mutate(x)}
            canAct={canAct}
            people={mineOnly
              .map((r) => ({ id: r.run.leadId, name: r.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name)))}
            noun="lead"
            idPrefix="su"
            onOpen={openLead}
            now={now}
            savedHideDone={hideDone}
            onSaveHideDone={(next) => savePrefs.mutate({ suHideDone: next })}
          />
          <section className={cn(PANEL, 'p-4')} aria-label="Signed today">
            <div className="mb-2.5 flex items-center gap-2">
              <PartyPopper size={14} className="text-emerald-600 dark:text-emerald-300" />
              <h3 className="mb-0 text-[14px] font-bold text-slate-800 dark:text-slate-100">Signed today</h3>
              <span className="ml-auto text-[12px] font-semibold tabular-nums text-slate-400">{signedToday.length}</span>
            </div>
            {signedShown.length ? (
              <div className="flex flex-col gap-1.5">
                {signedShown.map((w) => (
                  <SignedTodayRow key={`won-${w.cadenceRunId}`} win={w} />
                ))}
              </div>
            ) : (
              <p className="mb-0 py-1 text-center text-[12.5px] text-slate-400">
                {q && signedToday.length ? 'No matches for this search.' : 'Your signups today land here.'}
              </p>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default SigningUpDesk;
